import { useState } from "react";
import Webcam from "react-webcam";
import { motion } from "framer-motion";
import { Upload, Camera, Loader, Stethoscope, ShieldCheck, UserCheck } from "lucide-react";
import { Button } from "./Button";
import { Card, CardContent } from "./Card";
import axios from "axios";

export default function Home() {
  const [image, setImage] = useState(null);
  const [webcamActive, setWebcamActive] = useState(false);
  const webcamRef = useState(null);
  const [loading, setLoading] = useState(false);
  const [prediction, setPrediction] = useState(null);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImage(URL.createObjectURL(file));
    }
  };

  const captureImage = () => {
    const screenshot = webcamRef.current.getScreenshot();
    setImage(screenshot);
    setWebcamActive(false);
  };

  const handlePredict = async () => {
    if (!image) return;
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("image", image);
      const response = await axios.post("http://localhost:5000/predict", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPrediction(response.data);
    } catch (error) {
      console.error("Prediction error", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="min-h-screen bg-gray-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      {/* Hero Section */}
      <motion.header
        className="text-center py-16 bg-gradient-to-r from-blue-500 to-purple-500 text-white"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <h2 className="text-4xl py-16 font-bold">AI-Powered Skin Disease Detection</h2>
        <p className="mt-2 text-lg">Upload or capture an image to get instant AI-based results.</p>
      </motion.header>

      {/* Features Section */}
      <section className="py-12 px-6 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {[ 
          { Icon: Stethoscope, title: "Doctor Verified", text: "All AI predictions are reviewed by certified dermatologists." },
          { Icon: ShieldCheck, title: "100% Secure", text: "Your data is protected with high-level encryption." },
          { Icon: UserCheck, title: "Easy to Use", text: "Simple interface for fast and accurate predictions." }
        ].map((feature, index) => (
          <motion.div
            key={index}
            className="p-6 bg-white rounded-lg shadow-md flex flex-col items-center"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
          >
            <feature.Icon className="h-12 w-12 text-blue-600 mb-3" />
            <h3 className="text-xl font-semibold">{feature.title}</h3>
            <p className="text-gray-600 text-center">{feature.text}</p>
          </motion.div>
        ))}
      </section>

      {/* Prediction Card */}
      <section className="flex flex-col items-center py-12">
        <motion.div
          className="w-full max-w-lg p-6 bg-white rounded-lg shadow-md"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-semibold text-center mb-4 text-gray-800">Skin Disease Predictor</h2>

          <motion.label
            className="flex flex-col items-center justify-center p-4 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
            whileHover={{ scale: 1.05 }}
          >
            <Upload className="h-10 w-10 text-gray-500" />
            <span className="text-gray-600">Upload an Image</span>
            <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
          </motion.label>

          <Button className="w-full mt-4 flex items-center justify-center" onClick={() => setWebcamActive(!webcamActive)}>
            <Camera className="mr-2" /> {webcamActive ? "Close Camera" : "Capture Image"}
          </Button>

          {webcamActive && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
              <Webcam ref={webcamRef} screenshotFormat="image/jpeg" className="mt-4 w-full rounded-lg shadow-lg" />
            </motion.div>
          )}

          {image && (
            <motion.img
              src={image}
              alt="Uploaded"
              className="h-40 w-auto mx-auto rounded-lg shadow mt-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            />
          )}

          <Button className="w-full mt-4" onClick={handlePredict} disabled={!image || loading}>
            {loading ? <Loader className="animate-spin" /> : "Predict"}
          </Button>

          {prediction && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
              <CardContent className="mt-4 text-center">
                <h3 className="text-xl font-medium text-gray-800">Prediction: {prediction.disease}</h3>
                <p className="text-gray-600">Confidence: {prediction.confidence}%</p>
              </CardContent>
            </motion.div>
          )}
        </motion.div>
      </section>
    </motion.div>
  );
}
